<template>
  <form class="flex flex-col" @submit.prevent="login">
    <h2 class="mb-2">
      {{ $t("login.twoFactor.title") }}
    </h2>
    <span class="mb-2">
      {{ $t("login.twoFactor.description") }}
    </span>
    <InputField
      v-model="code"
      :error="errors"
      :pattern="/[0-9]+/"
      placeholder="565662"
      class="mb-5 mt-10"
    />
    <custom-button
      type="submit"
      variant="primary"
      class="w-full"
      :busy="loading"
    >
      {{ $t("login.twoFactor.buttonText") }}
    </custom-button>
  </form>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { FetchError, useFetchAction } from '@hooks/fetch';
import { useRouter } from 'vue-router';
import { string } from 'yup';
import { useI18n } from 'vue-i18n';
import { useLocalStorage } from '@vueuse/core';
import { tableFieldEntriesKey } from '@hooks/table-configuration';
import { enumOptionsCacheKey } from '@utils/table/hooks/provider';
import InputField from '@/components/utils/inputs/Input.vue';
import { setAuthToken } from '@/utils/tokenManager';
import { useStore } from '@/store';
import type {
  HydraCollection,
  TableFieldsEntry,
  User,
  WorkingTableFieldsEntry,
} from '@/entities/Entities';
import { useValidation } from '@/hooks/form';
import { useNotify } from '@/components/utils/notifications';

interface Props {
  email: string;
  password: string;
}

const TWO_FACTOR_ERROR_CODE = 'two_factor_wrong';

const code = ref('');
const props = defineProps<Props>();

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const [query, { loading }] = useFetchAction();

const notify = useNotify();

const { errors, validatedFn } = useValidation(
  string()
    .required(t('login.twoFactor.codeRequired'))
    .matches(/\d+/, t('login.twoFactor.codeNoDot'))
    .min(6, t('login.twoFactor.codeLength', { length: 6 }))
    .max(6, t('login.twoFactor.codeLength', { length: 6 })),
  code,
);

const login = validatedFn(async () => {
  let loggedInUser: User | undefined;

  try {
    const { token, user } = await query<{ token: string; user: User }>('/login', {
      method: 'POST',
      body: {
        email: props.email,
        password: props.password,
        twoFactorCode: code.value,
      },
    });

    if (token && user) {
      setAuthToken(token);
      store.commit('auth/SET_USER', user);
      loggedInUser = user;

      const tableFieldEntries = useLocalStorage<Record<string, WorkingTableFieldsEntry>>(tableFieldEntriesKey, {});
      tableFieldEntries.value = {};
      const members = (await query<HydraCollection<TableFieldsEntry>>('/table_fields_entries', {}))?.['hydra:member'] ?? [];
      tableFieldEntries.value = members.reduce((acc, member) => {
        acc[member.pathKey] = {
          id: member['@id'],
          activeFields: member.activeFields,
        };
        return acc;
      }, {} as Record<string, WorkingTableFieldsEntry>);

      // clear enums from local storage to prevent stale data
      const enumOptions = useLocalStorage<Record<string, string[]>>(enumOptionsCacheKey, {});
      enumOptions.value = null;
    }
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === TWO_FACTOR_ERROR_CODE) {
        errors.value = t('login.twoFactor.errorNotification');

        return;
      }
    }

    notify?.({
      title: t('serverError'),
      variant: 'error',
    });
    return;
  }

  if (loggedInUser) {
    if (router.currentRoute.value.redirectedFrom) {
      await router.push(router.currentRoute.value.redirectedFrom);
    } else {
      await router.push({ name: 'home', params: { locale: loggedInUser.language } });
    }
  }
});
</script>

<style lang="scss" scoped></style>
